
interface CalculationDetail {
  유닛_기대: number;
  유닛_실현: number;
  유닛_미납: number;
  케어_기대: number;
  케어_실현: number;
  케어_미납: number;
  패밀리_기대: number;
  패밀리_실현: number;
  패밀리_미납: number;
}

interface UnpaidCalculationChartProps {
  calculation: {
    formula: string;
    totalExpected: number;
    totalPaid: number;
    unpaidAmount: number;
    detail: CalculationDetail;
  };
}

export const UnpaidCalculationChart = ({
  calculation,
}: UnpaidCalculationChartProps) => {
  const chartData = [
    {
      category: '유닛',
      기대매출: calculation.detail.유닛_기대,
      실현매출: calculation.detail.유닛_실현,
      미납금액: calculation.detail.유닛_미납,
    },
    {
      category: '케어',
      기대매출: calculation.detail.케어_기대,
      실현매출: calculation.detail.케어_실현,
      미납금액: calculation.detail.케어_미납,
    },
    {
      category: '패밀리',
      기대매출: calculation.detail.패밀리_기대,
      실현매출: calculation.detail.패밀리_실현,
      미납금액: calculation.detail.패밀리_미납,
    },
  ];

  const formatCurrency = (value: number) => {
    if (value >= 100000000) {
      return `${(value / 100000000).toFixed(2)}억`;
    }
    if (value >= 10000) {
      return `${(value / 10000).toFixed(0)}만`;
    }
    return value.toLocaleString();
  };

  const unpaidRatio = (
    (calculation.unpaidAmount / calculation.totalExpected) *
    100
  ).toFixed(2);
  const collectionRate = (
    (calculation.totalPaid / calculation.totalExpected) *
    100
  ).toFixed(2);

  return (
    <div className="space-y-6">
      {/* 계산 공식 표시 */}
      <div className="p-4 rounded-lg border bg-slate-50">
        <p className="mb-2 text-sm text-gray-500">미납금 산출 공식</p>
        <p className="p-3 font-mono text-xs text-gray-600 bg-white rounded border">
          {calculation.formula}
        </p>
      </div>

      {/* 전체 요약 카드 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 text-center bg-blue-50 rounded-lg border border-blue-200">
          <p className="mb-1 text-sm text-blue-600">총 기대매출</p>
          <p className="text-2xl font-bold text-blue-700">
            {formatCurrency(calculation.totalExpected)}원
          </p>
        </div>
        <div className="p-4 text-center bg-green-50 rounded-lg border border-green-200">
          <p className="mb-1 text-sm text-green-600">총 실현매출</p>
          <p className="text-2xl font-bold text-green-700">
            {formatCurrency(calculation.totalPaid)}원
          </p>
          <p className="mt-1 text-xs text-green-500">
            수금률 {collectionRate}%
          </p>
        </div>
        <div className="p-4 text-center bg-red-50 rounded-lg border border-red-200">
          <p className="mb-1 text-sm text-red-600">총 미납금</p>
          <p className="text-2xl font-bold text-red-700">
            {formatCurrency(calculation.unpaidAmount)}원
          </p>
          <p className="mt-1 text-xs text-red-500">미납률 {unpaidRatio}%</p>
        </div>
      </div>

      {/* 상세 내역 테이블 */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium text-left text-gray-500">
                구분
              </th>
              <th className="px-4 py-3 font-medium text-right text-gray-500">
                기대매출
              </th>
              <th className="px-4 py-3 font-medium text-right text-gray-500">
                실현매출
              </th>
              <th className="px-4 py-3 font-medium text-right text-gray-500">
                미납금
              </th>
              <th className="px-4 py-3 font-medium text-right text-gray-500">
                수금률
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {chartData.map((row) => {
              const rate = ((row.실현매출 / row.기대매출) * 100).toFixed(2);
              return (
                <tr key={row.category}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {row.category}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-600">
                    {row.기대매출.toLocaleString()}원
                  </td>
                  <td className="px-4 py-3 text-right text-green-600">
                    {row.실현매출.toLocaleString()}원
                  </td>
                  <td className="px-4 py-3 font-medium text-right text-red-600">
                    {row.미납금액.toLocaleString()}원
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`font-medium ${parseFloat(rate) >= 99 ? 'text-green-600' : parseFloat(rate) >= 97 ? 'text-yellow-600' : 'text-red-600'}`}
                    >
                      {rate}%
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr className="font-semibold bg-gray-50">
              <td className="px-4 py-3 text-gray-900">합계</td>
              <td className="px-4 py-3 text-right text-blue-700">
                {calculation.totalExpected.toLocaleString()}원
              </td>
              <td className="px-4 py-3 text-right text-green-700">
                {calculation.totalPaid.toLocaleString()}원
              </td>
              <td className="px-4 py-3 text-right text-red-700">
                {calculation.unpaidAmount.toLocaleString()}원
              </td>
              <td className="px-4 py-3 text-right text-gray-700">
                {collectionRate}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 차트: 미납금 구성 비율 게이지 */}
      <div>
        <h4 className="mb-4 text-sm font-medium text-gray-700 text-center">미납금 구성 비율</h4>
        {(() => {
          const total = calculation.detail.유닛_미납 + calculation.detail.케어_미납 + calculation.detail.패밀리_미납;
          const items = [
            { name: '유닛', amount: calculation.detail.유닛_미납, color: '#3b82f6' },
            { name: '케어', amount: calculation.detail.케어_미납, color: '#10b981' },
            { name: '패밀리', amount: calculation.detail.패밀리_미납, color: '#8b5cf6' },
          ].filter(item => item.amount > 0);
          return (
            <div className="space-y-4">
              {items.map((item) => {
                const pct = total > 0 ? (item.amount / total) * 100 : 0;
                return (
                  <div key={item.name}>
                    <div className="flex justify-between items-center mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-gray-700">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: item.color }}>
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-7 bg-gray-100 rounded-md overflow-hidden">
                      <div
                        className="h-full rounded-md flex items-center justify-end pr-2 transition-all duration-500"
                        style={{
                          width: `${Math.max(pct, 1.5)}%`,
                          backgroundColor: item.color,
                        }}
                      >
                        {pct >= 10 && (
                          <span className="text-xs text-white font-medium">{pct.toFixed(1)}%</span>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 text-right">
                      {item.amount.toLocaleString()}원
                    </p>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* 미납금 상세 금액 */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="mb-3 text-sm font-medium text-gray-700">미납금 상세 금액</p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="w-3 h-3 rounded-full bg-blue-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500">유닛</p>
            <p className="text-sm font-semibold text-gray-900">{calculation.detail.유닛_미납.toLocaleString()}원</p>
          </div>
          <div>
            <div className="w-3 h-3 rounded-full bg-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500">케어</p>
            <p className="text-sm font-semibold text-gray-900">{calculation.detail.케어_미납.toLocaleString()}원</p>
          </div>
          <div>
            <div className="w-3 h-3 rounded-full bg-violet-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500">패밀리</p>
            <p className="text-sm font-semibold text-gray-900">{calculation.detail.패밀리_미납.toLocaleString()}원</p>
          </div>
        </div>
      </div>
    </div>
  );
};
